import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Sidebar = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const styles = {
    sidebar: {
      width: sidebarCollapsed ? '60px' : '250px',
      backgroundColor: '#2c3e50',
      color: 'white',
      padding: '20px 0',
      boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
      transition: 'width 0.3s ease',
      position: 'relative',
      height: 'calc(100vh - 111px)', // ปรับจาก 80px เป็น 111px (21px + 90px)
      overflowY: 'auto',
    },
    sidebarToggle: {
      position: 'absolute',
      top: '10px',
      right: '-15px',
      width: '30px',
      height: '30px',
      backgroundColor: '#3498db',
      border: 'none',
      borderRadius: '50%',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      zIndex: 10,
    },
    sidebarHeader: {
      padding: '0 20px 20px 20px',
      borderBottom: '1px solid #34495e',
      marginBottom: '20px',
      overflow: 'hidden',
    },
    sidebarTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '10px',
      whiteSpace: 'nowrap',
    },
    sidebarUser: {
      fontSize: '14px',
      color: '#bdc3c7',
      whiteSpace: 'nowrap',
    },
    sidebarMenu: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    sidebarMenuItem: {
      padding: '12px 20px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      textDecoration: 'none',
      color: 'inherit',
      position: 'relative',
    },
    sidebarMenuItemHover: {
      backgroundColor: '#34495e',
      transform: 'translateX(5px)',
    },
    sidebarMenuItemActive: {
      backgroundColor: '#3498db',
      borderLeft: '4px solid #ecf0f1',
    },
    sidebarIcon: {
      fontSize: '16px',
      width: '20px',
      textAlign: 'center',
      flexShrink: 0,
    },
    sidebarText: {
      fontSize: '14px',
      whiteSpace: 'nowrap',
      opacity: sidebarCollapsed ? 0 : 1,
      transition: 'opacity 0.3s ease',
    },
    tooltip: {
      position: 'absolute',
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: '#34495e',
      color: 'white',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.3s ease',
      zIndex: 1000,
      marginLeft: '10px',
    },
    tooltipVisible: {
      opacity: 1,
    },
  };

  const menuItems = [
    { path: '/dashboard', icon: '🏠', text: t('dashboard') },
    { path: '/my-trips', icon: '✈️', text: t('myTrips') },
    { path: '/edit-profile', icon: '👤', text: t('editProfile') },
    { path: '/analytics', icon: '📊', text: t('log') },
    { path: '/manage-subscription', icon: '💳', text: t('manageSubscription') },
    { path: '/help', icon: '❓', text: t('help') },
  ];

  // Add admin menu items for admin and superadmin users
  const adminMenuItems = [
    { path: '/admin', icon: '🔧', text: t('adminDashboard') },
  ];

  // Combine regular menu items with admin items for admin users
  const allMenuItems = user?.role === 'admin' || user?.role === 'superadmin' 
    ? [...menuItems, ...adminMenuItems]
    : menuItems;

  return (
    <div style={styles.sidebar}>
      <button 
        style={styles.sidebarToggle}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title={sidebarCollapsed ? t('expandSidebar') : t('collapseSidebar')}
      >
        {sidebarCollapsed ? '→' : '←'}
      </button>
      
      <div style={styles.sidebarHeader}>
        <div style={styles.sidebarTitle}>
          {!sidebarCollapsed && 'Mellow App'}
        </div>
        <div style={styles.sidebarUser}>
          {!sidebarCollapsed && `${t('hello')}, ${user?.username}`}
        </div>
      </div>
      
      <ul style={styles.sidebarMenu}>
        {allMenuItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          const isHovered = hoveredItem === index;
          
          return (
            <li key={index}>
              <Link 
                to={item.path} 
                style={{
                  ...styles.sidebarMenuItem,
                  ...(isActive && styles.sidebarMenuItemActive),
                  ...(isHovered && !isActive && styles.sidebarMenuItemHover)
                }}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span style={styles.sidebarIcon}>{item.icon}</span>
                <span style={styles.sidebarText}>{item.text}</span>
                {sidebarCollapsed && isHovered && (
                  <div style={{
                    ...styles.tooltip,
                    ...styles.tooltipVisible
                  }}>
                    {item.text}
                  </div>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar; 